// Phase 17 Plan 05 Task 2 — QueenRises cinematic overlay.
// Phase 18 Plan 04 Task 2 — POLISH-07 migration: inline aria-live region
// replaced with a useAnnounce() call on mount. The T12 RIVER announcement key
// is reused here per UI-SPEC §Copywriting (the queen-rises moment carries the
// "new runes revealed" announcement payload; see Plan 04 plan §migration sites
// — QueenRises is the T12 announcement vehicle on the prototype-driven path).

import { useStore } from '@nanostores/react'
import { type CSSProperties, type JSX, useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { playSfx } from '../../game/audio/useAudio'
import { $reducedMotion } from '../../stores/motion'
import { useAnnounce } from '../components/useAnnounce'

export interface QueenRisesProps {
  readonly onComplete: () => void
  readonly durationMs?: number
}

const PARTICLE_COUNT = 12

export function QueenRises({
  onComplete,
  durationMs = 1600,
}: QueenRisesProps): JSX.Element {
  const announce = useAnnounce()
  const intl = useIntl()
  const reducedMotion = useStore($reducedMotion)

  useEffect(() => {
    const id = window.setTimeout(onComplete, durationMs)
    return () => {
      window.clearTimeout(id)
    }
  }, [onComplete, durationMs])

  // The Queen organ — the brand's third signature sound (BrandIdentity §13).
  // Audio plays regardless of reduced motion (see stores/audio policy).
  useEffect(() => {
    playSfx('queen')
  }, [])

  // POLISH-07 — announce T12 reveal via the global polite region on mount.
  // The inline aria-live wrapper was replaced; <LiveRegion> in RootApp owns
  // the global announcement surface.
  useEffect(() => {
    announce(
      intl.formatMessage({ id: 'a11y.announce.reveal_t12_river' }),
      'polite',
    )
  }, [announce, intl])

  return (
    <div
      role="presentation"
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 60,
        // §10.4 — the Queen moment happens ON the board: a theme-magic bloom
        // centred on the piece, NOT a full-screen scrim. color-mix keeps it
        // theme-aware across all four palettes (THEME-04). Under reduced motion
        // the bloom is softened (no aura pulse).
        background: `radial-gradient(circle at 50% 56%, color-mix(in srgb, var(--color-magic) ${
          reducedMotion ? 35 : 55
        }%, transparent) 0%, transparent 35%)`,
      }}
    >
      {Array.from({ length: PARTICLE_COUNT }).map((_, i) => {
        const angle = (i / PARTICLE_COUNT) * Math.PI * 2
        const r = 60
        const dx = Math.cos(angle) * r
        const dy = Math.sin(angle) * r
        const particleStyle: CSSProperties = {
          position: 'absolute',
          left: '50%',
          top: '56%',
          width: 6,
          height: 6,
          borderRadius: 'var(--radius-pill)',
          background: 'var(--color-magic)',
          boxShadow: '0 0 12px var(--color-magic)',
          // RMA11Y-01/INT-06: the per-particle radial offset lives in CSS
          // custom props so the `queenSpark` keyframe (pieces.css) can OWN
          // `transform` and animate the spark outward. An inline
          // `transform: translate(...)` here would be clobbered the instant
          // the animation starts — which is exactly why the prior version's
          // sparks sat static (the keyframe referenced didn't exist, and even
          // once added would have fought an inline transform). The base
          // inline transform reads the same props so the resting position is
          // correct before the first animation frame AND under reduced motion
          // (where the animation is suppressed to 'none').
          ['--dx' as string]: `${dx}px`,
          ['--dy' as string]: `${dy}px`,
          transform: 'translate(var(--dx), var(--dy))',
          opacity: 0.85,
          // A11Y-QUEEN-RM-01: explicit reduced-motion fork — the sparks sit
          // static (no infinite pulse) rather than relying solely on the
          // globals.css animation cap.
          animation: reducedMotion
            ? 'none'
            : 'queenSpark 1.6s ease-out infinite',
          animationDelay: reducedMotion ? undefined : `${i * 80}ms`,
        }
        return (
          <span
            // biome-ignore lint/suspicious/noArrayIndexKey: 12 fixed positions, never reordered
            key={i}
            className="queen-spark"
            aria-hidden="true"
            style={particleStyle}
          />
        )
      })}
      <h1
        className="display-word gradient"
        style={{
          position: 'absolute',
          top: '40%',
          left: 0,
          right: 0,
          textAlign: 'center',
          fontFamily: 'var(--font-family-display)',
          fontWeight: 700,
          fontSize: 'var(--font-size-36)',
          letterSpacing: '-0.02em',
          margin: 0,
        }}
      >
        <FormattedMessage
          id="match.queen_rises.title"
          defaultMessage="THE QUEEN RISES"
        />
      </h1>
      <span
        style={{
          position: 'absolute',
          top: '52%',
          left: 0,
          right: 0,
          textAlign: 'center',
          fontFamily: 'var(--font-family-body)',
          fontSize: 'var(--font-size-14)',
          color: 'var(--color-text-secondary)',
        }}
      >
        <FormattedMessage
          id="match.queen_rises.subtitle"
          defaultMessage="Three turns of dominion."
        />
      </span>
    </div>
  )
}
